import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { FileDown, Loader2 } from 'lucide-react';
import { toast } from '@/components/ui/sonner';

const fmt = (v) => {
  if (v === undefined || v === null || isNaN(v)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
};
const pct = (v) => v !== undefined && v !== null ? `${(v * 100).toFixed(1)}%` : '—';

/**
 * Generic section PDF export button. Pass section data and it generates a standalone PDF.
 */
export default function SectionPdfExport({ title, analysisName, rows, columns }) {
  const [generating, setGenerating] = useState(false);

  const generate = useCallback(async () => {
    setGenerating(true);
    try {
      // Import jsPDF and autoTable plugin
      const jsPDFModule = await import('jspdf');
      const jsPDF = jsPDFModule.default;
      const autoTableModule = await import('jspdf-autotable');
      
      const doc = new jsPDF('p', 'mm', 'a4');
      
      // Attach autoTable to the document instance
      const autoTable = autoTableModule.default;
      
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();

      // Background
      doc.setFillColor(9, 9, 11);
      doc.rect(0, 0, pageW, pageH, 'F');

      // Header
      doc.setFillColor(99, 102, 241);
      doc.rect(15, 12, 3, 20, 'F');
      doc.setTextColor(250, 250, 250);
      doc.setFontSize(18);
      doc.text(title || 'Section Export', 23, 22);
      doc.setFontSize(9);
      doc.setTextColor(161, 161, 170);
      doc.text(`${analysisName || 'Analysis'} — ${new Date().toLocaleDateString()}`, 23, 30);

      // Table
      if (rows?.length > 0 && columns?.length > 0) {
        const tableHead = columns.map(c => c.header || '');
        const tableBody = rows.map(r => 
          columns.map(c => {
            try {
              return c.accessor ? String(c.accessor(r) ?? '—') : '—';
            } catch (e) {
              return '—';
            }
          })
        );

        autoTable(doc, {
          startY: 38,
          head: [tableHead],
          body: tableBody,
          theme: 'plain',
          styles: { fontSize: 8, textColor: [200, 200, 200], cellPadding: 3 },
          headStyles: { fillColor: [39, 39, 42], textColor: [161, 161, 170], fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [24, 24, 27] },
          margin: { left: 15, right: 15 },
        });
      }

      // Footer
      doc.setFontSize(7);
      doc.setTextColor(113, 113, 122);
      doc.text('AcquiFlow | Section Export', 15, pageH - 5);
      doc.text(`Page 1 of 1  |  Confidential`, pageW - 50, pageH - 5);

      doc.save(`${(analysisName || 'Analysis').replace(/\s+/g, '_')}_${(title || 'Section').replace(/\s+/g, '_')}.pdf`);
      toast.success(`${title || 'Section'} PDF exported`);
    } catch (e) {
      console.error('Section PDF error:', e);
      toast.error('Failed to export section');
    } finally {
      setGenerating(false);
    }
  }, [title, analysisName, rows, columns]);

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={generate}
      disabled={generating || !rows?.length}
      className="border-zinc-700 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800"
      data-testid={`export-${title.toLowerCase().replace(/\s+/g, '-')}-pdf`}
    >
      {generating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5 mr-1.5" />}
      Export PDF
    </Button>
  );
}

// Convenience helper: builds column defs from simple label/key pairs
export function buildColumns(defs) {
  return defs.map(d => ({
    header: d.label,
    accessor: (row) => {
      const val = typeof d.key === 'function' ? d.key(row) : row[d.key];
      if (val === undefined || val === null) return '—';
      if (d.format === 'currency') return fmt(val);
      if (d.format === 'percent') return pct(val);
      if (d.format === 'ratio') {
        const num = typeof val === 'number' ? val : parseFloat(val);
        return !isNaN(num) ? num.toFixed(2) + 'x' : '—';
      }
      return String(val);
    },
  }));
}
